/**
 * src/components/ProtectedRoute.js
 * 
 * Route protection component that enforces:
 * - Authentication (redirects to login if not authenticated)
 * - Email verification (for sensitive actions)
 * - Phone verification (for high-risk actions like selling)
 * - Redirect-after-login flow (returns user to intended page)
 * 
 * Usage:
 *   <ProtectedRoute
 *     requireEmailVerified={true}
 *     requirePhoneVerified={false}
 *   >
 *     <SensitivePage />
 *   </ProtectedRoute>
 * 
 * - Minimal interruption with clear messaging
 * - Direct path to verification if needed
 */

import React from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { useAdmin } from "../hooks/useAdmin";
import { AlertCircle, CheckCircle, Phone, ShieldOff } from "lucide-react";
import LoadingSpinner from "./ui/LoadingSpinner";

function ProtectedRoute({
  children,
  requireEmailVerified = false,
  requirePhoneVerified = false,
}) {
  const { t, ready: i18nReady } = useTranslation(["common", "auth"]);
  const location = useLocation();
  const { user, loading } = useAuth();
  const { banned } = useAdmin();

  const loadingText = i18nReady
    ? t("common:loading", "Loading...")
    : "Loading...";

  const tAuth = (key, fallback) => t(key, { ns: "auth", defaultValue: fallback });
  const tCommon = (key, fallback) => t(key, { ns: "common", defaultValue: fallback });

  // Still loading auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <LoadingSpinner size="large" text={loadingText} tone="primary" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    const redirectPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectPath}`} replace />;
  }

  // Account banned / suspended
  if (banned) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-red-200 dark:border-red-800 p-6 sm:p-8">
          <div className="flex justify-center mb-6">
            <div className="bg-red-100 dark:bg-red-900/30 rounded-full p-4">
              <ShieldOff className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-2">
            {t("auth.accountSuspended", "Account Suspended")}
          </h2>

          <p className="text-gray-600 dark:text-gray-400 text-sm text-center mb-6">
            {t("auth.accountSuspendedMessage", "Your account has been suspended due to a policy violation. If you believe this is an error, please contact support.")}
          </p>

          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-700 dark:text-red-300">
              {/* Current domain: orrith.me. Future domains: orrith.rw, orrith.com */}
              {t("auth.contactSupportEmail", "Contact: support@orrith.me")}
            </p>
          </div>

          <button
            onClick={() => {
              const { getAuth, signOut } = require("firebase/auth");
              signOut(getAuth()).then(() => window.location.href = "/login");
            }}
            className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
          >
            {t("auth.signOut", "Sign Out")}
          </button>
        </div>
      </div>
    );
  }

  // Email verification required but not verified
  if (requireEmailVerified && !user.emailVerified) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-neutral-900 rounded-2xl shadow-[0_24px_70px_rgba(0,0,0,0.45)] border border-neutral-700 p-6 sm:p-8">
          <div className="flex justify-center mb-6">
            <div className="bg-amber-100 dark:bg-amber-900/30 rounded-full p-4">
              <AlertCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white text-center mb-2">
            {tAuth("auth.emailVerificationRequired", "Email verification required")}
          </h2>

          <p className="text-gray-300 text-sm text-center mb-6">
            {tAuth("auth.emailVerificationMessage", "Verify your email to access all features.")}
          </p>

          <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-300">
              {tAuth("auth.checkEmailInbox", "Check your inbox for the verification email")}
            </p>
          </div>

          <Link
            to="/settings"
            className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors mb-3 block text-center"
          >
            {tAuth("auth.verifyEmail", "Verify email")}
          </Link>

          <Link
            to="/farm"
            className="w-full py-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium text-sm block text-center"
          >
            {tCommon("back", "Back")}
          </Link>
        </div>
      </div>
    );
  }

  // Phone verification required but not verified
  if (requirePhoneVerified && !user.hasPhoneVerified) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-neutral-900 rounded-2xl shadow-[0_24px_70px_rgba(0,0,0,0.45)] border border-neutral-700 p-6 sm:p-8">
          <div className="flex justify-center mb-6">
            <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full p-4">
              <Phone className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white text-center mb-2">
            {tAuth("auth.phoneVerificationRequired", "Phone verification required")}
          </h2>

          <p className="text-gray-300 text-sm text-center mb-6">
            {tAuth("auth.phoneVerificationMessage", "Please verify your phone number to continue")}
          </p>

          <Link
            to="/settings"
            className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors mb-3 block text-center"
          >
            {tAuth("auth.verifyPhone", "Verify phone")}
          </Link>

          <button
            onClick={() => window.history.back()}
            className="w-full py-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium text-sm"
          >
            {tCommon("back", "Back")}
          </button>
        </div>
      </div>
    );
  }

  // All checks passed - render children
  return children;
}

export default ProtectedRoute;

